import { useId, useRef, useState } from "react";
import { CoreIconButton } from "./Button";
import styles from "./PopoverButton.module.css";
import CoreExpandButton, { CoreExpandButtonProps, CoreExpandIcon } from "./ExpandButton";
import { Button, ButtonProps, Popover, PopoverProps, PopoverVirtualElement, Typography } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import { CoreXIcon } from "../icon";
import { TooltipWrapper } from "../../wrapper";

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type CorePopoverButtonProps = {
	title: React.ReactNode;
	children: React.ReactNode;
	slotProps?: {
		iconButton?: Omit<
			CoreExpandButtonProps,
			"aria-haspopup" | "aria-controls" | "aria-expanded" | "onClick" | "id" | "color" | "children"
		>;
		popover?: Omit<PopoverProps, "id" | "open" | "anchorEl" | "children">;
		button?: Omit<ButtonProps, "onClick" | "id" | "aria-haspopup" | "aria-controls" | "aria-expanded" | "children">;
		spanWrapperClassName?: string;
	};
	buttonText?: React.ReactNode;
	anchorEl?: PopoverProps["anchorEl"];
	tooltip?: React.ReactNode;
	maxWidth?: number;
	variant?: "icon" | "text";
};

export default function CorePopoverButton({
	title,
	children,
	slotProps,
	anchorEl,
	tooltip,
	maxWidth: propMaxWidth,
	variant = "icon",
	buttonText,
}: CorePopoverButtonProps) {
	const [open, setOpen] = useState(false);
	const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const paperRef = useRef<HTMLDivElement | null>(null);

	const buttonId = useId();
	const menuId = useId();

	const getResolvedAnchor = (): Element | PopoverVirtualElement | null => {
		if (!anchorEl) return buttonRef.current;
		return typeof anchorEl === "function" ? anchorEl() : anchorEl;
	};

	const handleToggleOpen = (nextOpen: boolean) => {
		if (nextOpen && anchorEl) {
			const resolvedAnchor = getResolvedAnchor();
			if (resolvedAnchor && "getBoundingClientRect" in resolvedAnchor) {
				const rect = resolvedAnchor.getBoundingClientRect();
				setAnchorWidth(rect.width);
			}
		}
		setOpen(nextOpen);
	};

	const customPaperClass = (slotProps?.popover?.slotProps?.paper as { className?: string })?.className ?? "";

	// MUI's FocusTrap wraps the popover in invisible tab-order sentinels which redirect focus back
	// inside whenever Tab lands on them, regardless of disableEnforceFocus (that flag only bails out
	// once focus already landed on something outside the trap that isn't a sentinel). So to let Tab
	// escape to the rest of the page instead of looping, we intercept Tab at the popover's real
	// boundary ourselves and move focus directly past the sentinel to whatever is next to the
	// trigger button in the page.
	const handlePaperKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== "Tab" || !paperRef.current) return;

		const focusable = Array.from(paperRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
		const isAtStart = event.shiftKey && document.activeElement === focusable[0];
		const isAtEnd = !event.shiftKey && document.activeElement === focusable[focusable.length - 1];
		if (!isAtStart && !isAtEnd) return;

		const trigger = buttonRef.current;
		if (!trigger) return;

		// Also exclude MUI's FocusTrap sentinel divs (siblings of the paper, not descendants, so the
		// paper-contains check above doesn't catch them) — they're tabbable but redirect focus back
		// into the trap the moment they're reached, which would undo the escape we're doing here.
		const pageFocusable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
			(el) =>
				(el === trigger || !paperRef.current?.contains(el)) &&
				el.dataset.testid !== "sentinelStart" &&
				el.dataset.testid !== "sentinelEnd",
		);
		const triggerIndex = pageFocusable.indexOf(trigger);
		const next = triggerIndex === -1 ? undefined : pageFocusable[triggerIndex + (isAtEnd ? 1 : -1)];

		if (next) {
			event.preventDefault();
			next.focus();
		}
	};

	const expandedTooltip = tooltip ?? slotProps?.iconButton?.expandedTooltip;
	const collapsedTooltip = tooltip ?? slotProps?.iconButton?.collapsedTooltip;

	const maxWidth = propMaxWidth ?? (anchorWidth ? `${anchorWidth}px` : undefined);
	const width = maxWidth ?? (anchorWidth ? `${anchorWidth}px` : undefined);

	return (
		<>
			{variant === "icon" ? (
				<CoreExpandButton
					variant="plain"
					{...slotProps?.iconButton}
					onChange={handleToggleOpen}
					className={`${styles.expandButton} ${open ? styles.expanded : ""} ${slotProps?.iconButton?.className ?? ""}`}
					aria-haspopup="dialog"
					aria-controls={open ? menuId : undefined}
					aria-expanded={open}
					id={buttonId}
					expanded={open}
					collapsedRight
					iconVariant="narrow"
					ref={buttonRef}
					expandedTooltip={expandedTooltip}
					collapsedTooltip={collapsedTooltip}
					defaultExpandedDirection="down"
					iconColor={"#874BD3"}
				/>
			) : (
				<TooltipWrapper
					title={tooltip}
					condition={!!tooltip}
					spanClassName={slotProps?.spanWrapperClassName}
					wrapInSpan={false}
				>
					<Button
						{...slotProps?.button}
						className={`${styles.expandButton} ${open ? styles.open : ""} ${slotProps?.button?.className ?? ""}`}
						aria-haspopup="dialog"
						aria-controls={open ? menuId : undefined}
						aria-expanded={open}
						aria-label={slotProps?.button?.["aria-label"] ?? (typeof tooltip === "string" ? tooltip : undefined)}
						onClick={() => handleToggleOpen(!open)}
						id={buttonId}
						ref={buttonRef}
						endIcon={
							<CoreExpandIcon
								expanded={open}
								defaultExpandedDirection="down"
								collapsedRight
								color="#874BD3"
								variant="narrow"
							/>
						}
					>
						{buttonText ?? title}
					</Button>
				</TooltipWrapper>
			)}
			<Popover
				{...slotProps?.popover}
				id={menuId}
				open={open}
				anchorEl={getResolvedAnchor()}
				onClose={() => {
					setOpen(false);
					slotProps?.popover?.onClose?.({}, "backdropClick");
				}}
				disableAutoFocus
				disableEnforceFocus
				TransitionProps={{
					...slotProps?.popover?.TransitionProps,
					onEntered: (...args) => {
						closeButtonRef.current?.focus();
						slotProps?.popover?.TransitionProps?.onEntered?.(...args);
					},
				}}
				slotProps={{
					...slotProps?.popover?.slotProps,
					paper: {
						...slotProps?.popover?.slotProps?.paper,
						ref: paperRef,
						onKeyDown: handlePaperKeyDown,
						className: `${styles.dropdownPaper} ${customPaperClass}`,
						role: "dialog",
						"aria-labelledby": buttonId,
						style: {
							maxWidth,
							width,
						},
					},
				}}
				anchorOrigin={{
					horizontal: "left",
					vertical: "bottom",
					...slotProps?.popover?.anchorOrigin,
				}}
			>
				<div className={styles.listTitle}>
					<Typography component="h2">{title}</Typography>
					<CoreIconButton
						ref={closeButtonRef}
						onClick={() => setOpen(false)}
						variant="plain"
						aria-label={i18nPackage.t("coreComponents.modal.close")}
					>
						<CoreXIcon width={20} height={20} ariaHidden="true" />
					</CoreIconButton>
				</div>

				{children}
			</Popover>
		</>
	);
}
